import { useTranslation } from 'react-i18next'
import { UnitLabel } from '@/shared/config/config'
import {
  InputGroup,
  Button,
  CardTitle,
  Card,
  CardHeader,
  CardBody,
  DataBlock
} from '@/common'
import Drawer from '@/shared/components/ui/Drawer'
import calculateOutputBasedClinkerEmissions from '@/features/reports/formulas/calculateOutputBasedClinkerEmissions'
import { useState, useCallback, useMemo } from 'react'
import { Artifacts } from '@/api/EmissionReportModel'
import { usePermissions } from '@/shared/hooks/usePermissions'

type State = {
  amountOfCementDeliveries: string //CemDeliv, tonnes
  cementStockVariations: string //CemSv, tonnes
  clinkerToCementRatio: string //CCR, tonnes clinker per tonne cement
  amountOfClinkerSupplied: string //Clis, tonnes
  amountOfClinkerDispatched: string //Clid, tonnes
  amountOfClinkerStockVariations: string //Clisv, tonnes
  emissionFactor: string //EF, tonnes CO2e/tonne clinker
}

const getInitialState = (initialValues?: Artifacts) => ({
  amountOfCementDeliveries: initialValues?.amountOfCementDeliveries || '',
  cementStockVariations: initialValues?.cementStockVariations || '',
  clinkerToCementRatio: initialValues?.clinkerToCementRatio || '',
  amountOfClinkerSupplied: initialValues?.amountOfClinkerSupplied || '',
  amountOfClinkerDispatched: initialValues?.amountOfClinkerDispatched || '',
  amountOfClinkerStockVariations: initialValues?.amountOfClinkerStockVariations || '',
  emissionFactor: initialValues?.emissionFactor || '0.525'
})

export default function CementOutputBasedCalculatorPanel({
  isOpen,
  close,
  onSubmit,
  initialValues
}: {
  isOpen: boolean
  close: () => void
  onSubmit: (emission: number, artifacts: Artifacts) => void
  initialValues: Artifacts
}) {
  const { t } = useTranslation()
  const { checkPermission } = usePermissions()
  const canEditEmissionReport = checkPermission('emissionReport', 'update')
  const [state, setState] = useState<State>(getInitialState(initialValues))

  const handleChange = useCallback((key: keyof State, value: string) => {
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const emission = useMemo(
    () =>
      calculateOutputBasedClinkerEmissions({
        amountOfCementDeliveries: Number(state.amountOfCementDeliveries),
        cementStockVariations: Number(state.cementStockVariations),
        clinkerToCementRatio: Number(state.clinkerToCementRatio),
        amountOfClinkerDispatched: Number(state.amountOfClinkerDispatched),
        amountOfClinkerSupplied: Number(state.amountOfClinkerSupplied),
        amountOfClinkerStockVariations: Number(state.amountOfClinkerStockVariations),
        emissionFactor: Number(state.emissionFactor)
      }),
    [state]
  )

  const onAddClick = useCallback(() => {
    onSubmit(emission, state)
    close()
  }, [close, emission, onSubmit, state])

  return (
    <Drawer isOpen={isOpen} close={close}>
      <Card>
        <CardHeader>
          <CardTitle title={t('report:calculators.clinkerOutputBasedCalculatorPanel.title')} />
        </CardHeader>
        <CardBody className="space-y-4">
          <InputGroup
            name="amountOfCementDeliveries"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.amountOfCementDeliveries.label'
            )}
            value={state.amountOfCementDeliveries}
            onChange={evt => handleChange('amountOfCementDeliveries', evt.target.value)}
            suffix={UnitLabel.t}
          />
          <InputGroup
            name="cementStockVariations"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.cementStockVariations.label'
            )}
            value={state.cementStockVariations}
            onChange={evt => handleChange('cementStockVariations', evt.target.value)}
            suffix={UnitLabel.t}
          />
          <InputGroup
            name="clinkerToCementRatio"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.clinkerToCementRatio.label'
            )}
            value={state.clinkerToCementRatio}
            onChange={evt => handleChange('clinkerToCementRatio', evt.target.value)}
            suffix={UnitLabel.tCliPerTCem}
          />
          <InputGroup
            name="amountOfClinkerSupplied"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.amountOfClinkerSupplied.label'
            )}
            value={state.amountOfClinkerSupplied}
            onChange={evt => handleChange('amountOfClinkerSupplied', evt.target.value)}
            suffix={UnitLabel.t}
          />
          <InputGroup
            name="amountOfClinkerDispatched"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.amountOfClinkerDispatched.label'
            )}
            value={state.amountOfClinkerDispatched}
            onChange={evt => handleChange('amountOfClinkerDispatched', evt.target.value)}
            suffix={UnitLabel.t}
          />
          <InputGroup
            name="amountOfClinkerStockVariations"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.amountOfClinkerStockVariations.label'
            )}
            value={state.amountOfClinkerStockVariations}
            onChange={evt => handleChange('amountOfClinkerStockVariations', evt.target.value)}
            suffix={UnitLabel.t}
          />
          <InputGroup
            name="emissionFactor"
            label={t(
              'report:calculators.clinkerOutputBasedCalculatorPanel.fields.emissionFactor.label'
            )}
            value={state.emissionFactor}
            onChange={evt => handleChange('emissionFactor', evt.target.value)}
            suffix={UnitLabel.tCO2ePerTCli}
          />

          <DataBlock label={t('report:common.emission')} data={`${emission || 0} ${UnitLabel.tCO2}`} />
          <footer className="flex justify-end space-x-4">
            <Button className="min-w-24" onClick={close} variant="subtle">
              {t('common:actions.cancel')}
            </Button>
            <Button
              className="w-24"
              disabled={!emission || emission === 0 || !canEditEmissionReport}
              onClick={onAddClick}
            >
              {t('common:actions.add')}
            </Button>
          </footer>
        </CardBody>
      </Card>
    </Drawer>
  )
}
